import { Css, ScrollableParent, useComputed } from "@homebound/beam";
import { useFormState } from "@homebound/form-state";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { StepperProvider, stepperBarHeight, useStepperContext } from "src/components/stepper";
import { PlanBasedBidPackageDetailsStep } from "src/routes/bids/plan-based-bid-package/components/1.PlanBasedBidPackageDetailsStep";
import { ConfigureBidGroupsStep } from "src/routes/bids/plan-based-bid-package/components/2.ConfigureBidGroupsStep";
import { ReviewAndDownloadStep } from "src/routes/bids/plan-based-bid-package/components/3.ReviewAndDownloadStep";
import { PlanBasedBidPackageStepActions } from "src/routes/bids/plan-based-bid-package/components/PlanBasedBidPackageStepActions";
import {
  PlanBasedBidPackagePageQuery,
  PlanBasedBidPackagePage_BidPackageFragment,
  PlanBasedBidPackagePage_OriginatingProjectFragment,
  usePlanBasedBidPackagePageQuery,
  usePlanBasedBidPackagePage_OriginatingProjectQuery,
} from "src/routes/bids/plan-based-bid-package/PlanBasedBidPackagePage.generated";
import {
  PlanBasedBidPackageFormState,
  PlanBasedBidPackageStep,
  initialSteps,
  planBasedBidPackageFormConfig,
} from "src/routes/bids/plan-based-bid-package/utils";
import { BidPackageParams } from "src/routes/routesDef";
import { isEmptyObject, queryResult } from "src/utils";
import { StringParam, useQueryParams } from "use-query-params";

export function PlanBasedBidPackagePage() {
  const { bidPackageGroupId } = useParams<BidPackageParams>();
  const [{ projectId }] = useQueryParams({ projectId: StringParam });
  const query = usePlanBasedBidPackagePageQuery({
    variables: { id: bidPackageGroupId || "" },
    skip: !bidPackageGroupId,
  });
  const originatingProjectQuery = usePlanBasedBidPackagePage_OriginatingProjectQuery({
    variables: { id: projectId! },
    skip: !!bidPackageGroupId || !projectId,
  });

  if (!bidPackageGroupId) {
    return projectId ? (
      queryResult(originatingProjectQuery, ({ project }) => (
        <StepperProvider steps={initialSteps(true)}>
          <PlanBasedBidPackageView originatingProject={project} />
        </StepperProvider>
      ))
    ) : (
      <StepperProvider steps={initialSteps(true)}>
        <PlanBasedBidPackageView />
      </StepperProvider>
    );
  }

  return queryResult(query, ({ bidPackageGroup }) => (
    <StepperProvider steps={initialSteps(false)}>
      <PlanBasedBidPackageView bidPackageGroup={bidPackageGroup} />
    </StepperProvider>
  ));
}

type PlanBasedBidPackageViewProps = {
  bidPackageGroup?: PlanBasedBidPackagePageQuery["bidPackageGroup"];
  originatingProject?: PlanBasedBidPackagePage_OriginatingProjectFragment;
};

function PlanBasedBidPackageView(props: PlanBasedBidPackageViewProps) {
  const { bidPackageGroup, originatingProject } = props;
  const { currentStep, setSteps } = useStepperContext();
  const formState = useFormState({
    config: planBasedBidPackageFormConfig,
    init: {
      input: bidPackageGroup,
      map: (bpg) => mapBidPackageToForm(bpg.bidPackages.first!),
      ifUndefined: {
        marketId: originatingProject?.market?.id,
        projects: originatingProject ? [originatingProject.id] : [],
        bidPlanGroups: [{ order: 0, dimensions: [] }],
      },
    },
  });

  useComputed(() => {
    if (formState.dirty && !isEmptyObject(formState.changedValue)) {
      const coreFieldsEmpty =
        !formState.marketId.value || !formState.costCodes.value?.nonEmpty || !formState.costType.valid;
      setSteps((prevState) => [
        { ...prevState[0], disabled: false },
        { ...prevState[1], disabled: coreFieldsEmpty },
        { ...prevState[2], disabled: coreFieldsEmpty },
      ]);
    }
  }, [formState]);

  const step = useMemo(() => {
    switch (currentStep.value) {
      case PlanBasedBidPackageStep.DEFINE_PACKAGE:
        return <PlanBasedBidPackageDetailsStep formState={formState} originatingProject={originatingProject} />;
      case PlanBasedBidPackageStep.CONFIGURE_BID_GROUPS:
        return <ConfigureBidGroupsStep formState={formState} />;
      case PlanBasedBidPackageStep.REVIEW_AND_DOWNLOAD:
        return <ReviewAndDownloadStep />;
      default:
        return <PlanBasedBidPackageDetailsStep formState={formState} originatingProject={originatingProject} />;
    }
  }, [currentStep.value, formState, originatingProject]);

  return (
    <ScrollableParent px={8} xss={Css.mbPx(stepperBarHeight).$}>
      {step}
      <PlanBasedBidPackageStepActions formState={formState} />
    </ScrollableParent>
  );
}

function mapBidPackageToForm(bidPackage: PlanBasedBidPackagePage_BidPackageFragment): PlanBasedBidPackageFormState {
  return {
    bidPackageGroupId: bidPackage.bidPackageGroup?.id,
    bidPackageId: bidPackage.id,
    marketId: bidPackage.projects.first?.market?.id,
    projects: bidPackage.projects.map((p) => p.id),
    costCodes: bidPackage.costCodes.map((cc) => cc.id),
    costType: bidPackage.costType.code,
    productOfferings: bidPackage.productOfferings.map((po) => po.id),
    requests: bidPackage.requests.map((r) => ({
      id: r.id,
      tradePartnerId: r.tradePartner.id,
      archive: !!r.archivedAt,
    })),
    bidPlanGroups: bidPackage.bidPlanGroups.map((bpg) => ({
      id: bpg.id,
      globalOptionId: bpg.globalOption?.id,
      order: bpg.order,
      dimensions: bpg.dimensions.map((d) => ({
        id: d.id,
        globalOptionTypeId: d.globalOptionType.id,
        globalOptionIds: d.globalOptions.map((go) => go.id),
      })),
    })),
  };
}
